import { Button } from "@medme/ui"
import { CalendarDays, Download, Loader2, Search } from "lucide-react"
import { useId } from "react"
import { useTranslation } from "react-i18next"
import { isLeadStatus, LEAD_STATUS_KEYS } from "../model/constants"
import type { LeadsFiltersModel } from "../model/use-leads-filters"

type LeadsFiltersProps = {
	filters: LeadsFiltersModel
	canExport: boolean
	isExporting: boolean
	onExport: () => void
}

export function LeadsFilters({
	filters,
	canExport,
	isExporting,
	onExport,
}: LeadsFiltersProps) {
	const { t } = useTranslation()
	const searchId = useId()
	const statusId = useId()
	const createdFromId = useId()
	const createdToId = useId()
	const controlClassName =
		"w-full rounded-[10px] border border-mm-border bg-mm-muted-2 px-3 py-2.5 text-sm outline-none transition-colors focus-visible:border-mm-primary focus-visible:ring-2 focus-visible:ring-mm-primary/20"
	const labelClassName =
		"text-[11px] font-semibold uppercase tracking-wide text-mm-muted-fg"

	return (
		<div className="grid grid-cols-1 items-end gap-3 rounded-2xl border bg-mm-card p-4 shadow-xs md:grid-cols-2 xl:grid-cols-12">
			<label className="grid gap-1.5 xl:col-span-4" htmlFor={searchId}>
				<span className={labelClassName}>{t("leads.filters.search")}</span>
				<div className="relative">
					<Search
						aria-hidden="true"
						className="absolute top-1/2 start-3 size-4 -translate-y-1/2 text-mm-muted-fg"
					/>
					<input
						id={searchId}
						type="search"
						className={`${controlClassName} ps-9`}
						placeholder={t("leads.filters.searchPlaceholder")}
						value={filters.search}
						onChange={(event) => filters.setSearch(event.target.value)}
					/>
				</div>
			</label>
			<label className="grid gap-1.5 xl:col-span-2" htmlFor={statusId}>
				<span className={labelClassName}>{t("leads.fields.status")}</span>
				<select
					id={statusId}
					className={controlClassName}
					value={filters.status}
					onChange={(event) => {
						const value = event.target.value
						if (value === "" || isLeadStatus(value)) filters.setStatus(value)
					}}
				>
					<option value="">{t("leads.filters.allStatuses")}</option>
					{Object.entries(LEAD_STATUS_KEYS).map(([value, key]) => (
						<option key={value} value={value}>
							{t(key)}
						</option>
					))}
				</select>
			</label>
			<fieldset className="grid gap-1.5 border-0 p-0 md:col-span-2 xl:col-span-4">
				<legend className={labelClassName}>{t("leads.filters.created")}</legend>
				<div className="rounded-[10px] border border-mm-border bg-mm-muted-2 px-3 py-2 focus-within:border-mm-primary focus-within:ring-2 focus-within:ring-mm-primary/20 sm:flex sm:items-center sm:gap-2 sm:py-0">
					<CalendarDays
						aria-hidden="true"
						className="hidden size-4 shrink-0 text-mm-muted-fg sm:block"
					/>
					<div className="grid w-full grid-cols-1 gap-2 sm:flex sm:items-center">
						<label
							className="text-[11px] text-mm-muted-fg sm:sr-only"
							htmlFor={createdFromId}
						>
							{t("leads.filters.createdFrom")}
						</label>
						<input
							id={createdFromId}
							type="date"
							className="w-full flex-1 border-0 bg-transparent py-1 text-sm outline-none sm:py-2.5"
							value={filters.createdFrom}
							max={filters.createdTo || undefined}
							onChange={(event) => filters.setCreatedFrom(event.target.value)}
						/>
						<span
							aria-hidden="true"
							className="hidden text-mm-muted-fg sm:inline"
						>
							—
						</span>
						<label
							className="text-[11px] text-mm-muted-fg sm:sr-only"
							htmlFor={createdToId}
						>
							{t("leads.filters.createdTo")}
						</label>
						<input
							id={createdToId}
							type="date"
							className="w-full flex-1 border-0 bg-transparent py-1 text-sm outline-none sm:py-2.5"
							value={filters.createdTo}
							min={filters.createdFrom || undefined}
							onChange={(event) => filters.setCreatedTo(event.target.value)}
						/>
					</div>
				</div>
			</fieldset>
			<Button
				className="h-auto w-full self-end rounded-[10px] bg-black py-2.5 text-white hover:bg-black/85 xl:col-span-2"
				disabled={!canExport || isExporting}
				onClick={onExport}
			>
				{isExporting ? <Loader2 className="animate-spin" /> : <Download />}
				{t("leads.actions.export")}
			</Button>
		</div>
	)
}
